import { usePartDefinition, useProductStoreContext } from "@project/emerge";
import { useMemo } from "react";
import * as THREE from "three";
import { PaddingStyle } from "../enum/PaddingStyle";
import { LTPartType } from "../enum/PartType";
import {
  padGeometryCache,
  padOutlineCache,
} from "../geometry/FinishedGeometryCache";
import {
  createFoamBlockGeometry,
  createFoamBlockOutline,
} from "../geometry/foamBlockGeometry";
import {
  createSlottedPadGeometry,
  createSlottedPadOutline,
  slottedPadHasGrooves,
} from "../geometry/slottedPadGeometry";
import {
  createSpikedPadGeometry,
  createSpikedPadOutline,
  spikedPadHasNubs,
} from "../geometry/spikedPadGeometry";
import type { PadDefinition } from "../interfaces/PadProperties";
import {
  getEggboxFoamMaterial,
  getGroovedFoamMaterial,
  getSlottedFoamMaterial,
  getSoftFoamMaterial,
  getSpikedFoamMaterial,
  getStandardFoamMaterial,
} from "../materials/FoamMaterials";
import {
  createPanelGeometryWithHoles,
  filterDuplicateCutouts,
  panelGeometryKey,
} from "./PanelRenderer";
import { PartEdges } from "./PartEdges";
import { useLatestCommittedResource } from "./useLatestCommittedResource";

/**
 * What a part that is not a pad but reached this renderer is drawn as.
 *
 * Module scope, because the alternative — building a `Group`, a
 * `BoxGeometry` and a `MeshStandardMaterial` in the render body and handing
 * them to `<primitive>` — allocates a set per render and R3F disposes none of
 * them, so a part in this state leaked buffers for as long as the page lived.
 */
const PLACEHOLDER_GEOMETRY = new THREE.BoxGeometry(50, 10, 50);
const PLACEHOLDER_MATERIAL = new THREE.MeshStandardMaterial({
  color: "#ff0000",
});

type PadGeometryKind = "cutout" | "spiked" | "grooved" | "block";

export const PadRenderer = ({ id }: { id: string }) => {
  const partDef = usePartDefinition<PadDefinition & { hidden?: boolean }>({
    id,
    select: [
      "size",
      "strength",
      "style",
      "hidden",
      "type",
      "cutouts",
      "position",
      "side",
      "orientation",
    ] as const,
  });

  const { uniqueCutouts } = useMemo(
    () => filterDuplicateCutouts(partDef?.cutouts ?? []),
    [partDef?.cutouts],
  );

  const hasCutouts = uniqueCutouts.length > 0;
  const thickness = partDef?.strength ?? 0;
  let geometryKind: PadGeometryKind;
  if (hasCutouts) {
    geometryKind = "cutout";
  } else if (
    partDef?.style === PaddingStyle.EGGBOX &&
    spikedPadHasNubs(thickness)
  ) {
    geometryKind = "spiked";
  } else if (
    partDef?.style === PaddingStyle.SLOTTED &&
    slottedPadHasGrooves(thickness)
  ) {
    geometryKind = "grooved";
  } else {
    geometryKind = "block";
  }

  // Cache the shape, not its finish: standard, soft and thin fallbacks are
  // the same block, and every cutout style is the same panel extrusion.
  // Plain pads ignore placement. Cutouts use effective panel coordinates so
  // moving a pad together with its holes also keeps the finished geometry.
  const geometryKey = useMemo(() => {
    if (
      partDef?.type !== LTPartType.Pad ||
      !partDef.size ||
      !partDef.position ||
      partDef.side === undefined ||
      partDef.strength === undefined
    ) {
      return null;
    }
    const w = partDef.size[0] ?? 100;
    const h = partDef.size[1] ?? 100;
    return geometryKind === "cutout"
      ? `cutout|${panelGeometryKey(
          new THREE.Vector2(w, h),
          partDef.position,
          partDef.side,
          uniqueCutouts,
          partDef.strength,
        )}`
      : `${geometryKind}|${w},${h}|${partDef.strength}`;
  }, [
    geometryKind,
    partDef?.type,
    partDef?.size,
    partDef?.position,
    partDef?.side,
    partDef?.strength,
    uniqueCutouts,
  ]);

  // Leased rather than memoised: these are the geometries this branch made
  // expensive — a 500 x 400 Noppenpolster is about 43k triangles — and a
  // memoised geometry is never disposed by R3F, so every resize tick used to
  // strand a set of buffers on the GPU for the lifetime of the page. The cache
  // disposes an entry only once nothing holds it, so dragging a pad through a
  // hundred sizes and back costs one upload per distinct size instead of a
  // hundred permanent ones.
  //
  // The result goes onto the mesh as a `geometry` prop, never as a
  // `<primitive attach>`: equal keys share one cached object between pads.
  const padGeometry = useLatestCommittedResource(geometryKey, () => {
    // A non-null key guarantees the placement fields exist.
    if (!partDef?.size || !partDef.position || partDef.side === undefined) {
      throw new Error("Missing pad geometry inputs");
    }
    const w = partDef.size[0] ?? 100;
    const h = partDef.size[1] ?? 100;
    const str = partDef.strength ?? 0;
    const key = geometryKey as string;
    if (hasCutouts) {
      const pos = partDef.position;
      const side = partDef.side;
      return padGeometryCache.acquire(key, () =>
        createPanelGeometryWithHoles(
          new THREE.Vector2(w, h),
          pos,
          side,
          uniqueCutouts,
          str,
        ),
      );
    }
    return padGeometryCache.acquire(key, () => {
      if (geometryKind === "spiked") {
        return createSpikedPadGeometry(w, str, h);
      }
      if (geometryKind === "grooved") {
        return createSlottedPadGeometry(w, str, h);
      }
      return createFoamBlockGeometry(w, str, h);
    });
  });

  // `outline` is drawn where the mesh's own edges cannot be used: the nubs of a
  // Noppenpolster are all sharper than the edge threshold, every groove edge
  // of a slotted pad is a right angle, and the rounding on a plain block is
  // all softer than it. A pad with cutouts is a plain extrusion and outlines
  // itself.
  // `showEdges` is in the key, not just in `PartEdges`: with edges off the
  // outline is never drawn, and an unconditional lease would still build one
  // per distinct pad size and hold it at a non-zero reference count, where the
  // cache's idle budget can never reach it.
  const useProductStore = useProductStoreContext();
  const showEdges = useProductStore((state) => state.showEdges);
  const outlineKey =
    geometryKey === null || hasCutouts || !showEdges
      ? null
      : `${geometryKey}|outline`;
  const padOutline = useLatestCommittedResource(outlineKey, () => {
    const w = partDef?.size?.[0] ?? 100;
    const h = partDef?.size?.[1] ?? 100;
    const str = partDef?.strength ?? 0;
    return padOutlineCache.acquire(outlineKey as string, () => {
      if (geometryKind === "spiked") {
        return createSpikedPadOutline(w, str, h);
      }
      if (geometryKind === "grooved") {
        return createSlottedPadOutline(w, str, h);
      }
      return createFoamBlockOutline(w, str, h);
    });
  });

  // Shared per style, and scaled from the pad's own coordinates rather than
  // from its size: the pores are a property of the foam, not of the piece it
  // was cut out of.
  const materialReady = geometryKey !== null && padGeometry !== null;
  const material = useMemo(() => {
    // Keep hook order stable, but do not bake foam for absent/invalid parts
    // or for geometry whose commit-time lease has not been acquired yet.
    if (!materialReady) return undefined;
    switch (partDef?.style) {
      case PaddingStyle.EGGBOX:
        // The spiked geometry already carries the nubs; only the pad shapes
        // that could not be displaced still need them painted on. Thickness is
        // in the test as well as cutouts, because a pad thinner than the base
        // slab has no nub left to stand on it and `createSpikedPadGeometry`
        // quietly hands back a plain block — which the modelled-nub material
        // paints nothing onto, so the pad would render as featureless foam.
        // The painted version is handed the thickness because that is what
        // sets the nub height, and it has to be the height of the nubs on the
        // pad next to it.
        return hasCutouts || !spikedPadHasNubs(thickness)
          ? getEggboxFoamMaterial(thickness)
          : getSpikedFoamMaterial();
      case PaddingStyle.SOFT:
        return getSoftFoamMaterial();
      case PaddingStyle.SLOTTED:
        // Same split as the eggbox, including the thin-sheet fallback: real
        // grooves where the pad could be cut, painted ones where it could not.
        if (hasCutouts || !slottedPadHasGrooves(thickness)) {
          return getSlottedFoamMaterial(hasCutouts ? "z" : "y");
        }
        return getGroovedFoamMaterial();
      default:
        return getStandardFoamMaterial();
    }
  }, [materialReady, partDef?.style, hasCutouts, thickness]);

  if (!partDef) return null;

  const { hidden, type } = partDef;

  if (type !== LTPartType.Pad) {
    return (
      <group visible={hidden !== true}>
        <mesh geometry={PLACEHOLDER_GEOMETRY} material={PLACEHOLDER_MATERIAL} />
      </group>
    );
  }

  if (!padGeometry) return null;

  const orientationDeg = partDef.orientation ?? 0;
  const orientationRad = (orientationDeg * Math.PI) / 180;

  /** Part position is slab center (see `applyPadVolumeCenterWorldOffset` in build steps); mesh stays at origin. */
  const meshProps = {
    rotation: hasCutouts
      ? ([Math.PI / 2, 0, orientationRad] as [number, number, number])
      : ([0, 0, 0] as [number, number, number]),
    position: [0, 0, 0] as [number, number, number],
  };

  return (
    <group visible={hidden !== true}>
      <mesh {...meshProps} geometry={padGeometry} material={material}>
        {hasCutouts ? (
          <PartEdges />
        ) : (
          padOutline && <PartEdges geometry={padOutline} />
        )}
      </mesh>
    </group>
  );
};
